import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Dialog, DialogContent, CircularProgress } from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import { toast } from 'react-toastify';
import heic2any from 'heic2any';

import DialogHeader from './DialogHeader';
import { Hr } from 'components/DeliveryManager';
import { ImagesThumb } from '../components/ImagesThumb';
import { DialogActionButton, DialogFooter } from 'components/Dialog';

import api from 'utils/api';
import useSearchParams from 'hooks/useSearchParams';
import { IMAGE_TYPES } from 'constants/images';
import { convertImage } from 'utils/image';

const PREFIX = 'PreferPhotoDialog';

const classes = {
  imageContainer: `${PREFIX}-imageContainer`,
  text: `${PREFIX}-text`,
  text2: `${PREFIX}-text2`,
  btn: `${PREFIX}-btn`,
  image: `${PREFIX}-image`,
  add: `${PREFIX}-add`,
  loading: `${PREFIX}-loading`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.imageContainer}`]: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    border: `1px dashed ${theme.colorTokens.outlineVariant}`,
    cursor: 'pointer',
  },

  [`& .${classes.text}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '14px',
    marginTop: 0,
  },

  [`& .${classes.text2}`]: {
    color: '#626262',
    marginTop: 10,
  },

  [`& .${classes.btn}`]: {
    borderRadius: '2rem',
    marginLeft: '0 !important',
  },

  [`& .${classes.image}`]: {
    width: '100%',
    objectFit: 'cover',
    marginBottom: '0.5rem',
  },

  [`& .${classes.add}`]: {
    display: 'block',
    fontSize: '14px',
    color: '#4a90e2',
    textAlign: 'center',
    padding: '0.5rem 0',
    backgroundColor: 'transparent',
    border: 'none',
    textDecoration: 'underline',
    textUnderlineOffset: '0.2rem',
    cursor: 'pointer',
  },

  [`& .${classes.loading}`]: {
    left: '40%',
    position: 'absolute',
  },
}));

const PreferPhotoDialog = compose(
  inject('store'),
  observer,
)(({ store, setFinishUpFeedback, setIsOpeningPreferredPhoto }) => {
  const [loading, setLoading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  const { trackingCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const skipQuestions = searchParams.get('skip_questionnaire');

  const { deliveryStore } = store;
  const { delivery, isOpenPreferredPhoto } = deliveryStore;
  const preferredPhotos = delivery?.preferred_photo || [];
  const photos = preferredPhotos.map((url) => ({ url }));
  const [imageList, setImageList] = useState(photos);

  const handleChange = async (e) => {
    const image = e.target.files?.[0];
    if (!image) return;

    const type = image.type;
    if (!IMAGE_TYPES.includes(type)) {
      toast.error('Image type is not supported');
      return;
    }

    setIsConverting(true);
    if (!['image/heic', 'image/heif'].includes(type)) {
      const result = await convertImage(image, 'jpeg');
      setImageList([...imageList, { url: result }]);
      setIsConverting(false);
      return;
    }

    let blobURL = URL.createObjectURL(image);
    let blobRes = await fetch(blobURL);
    let blob = await blobRes.blob();
    let result = await heic2any({
      blob,
      toType: 'image/jpeg',
      quality: 0.8,
    });

    setImageList([...imageList, { url: result }]);
    setIsConverting(false);
  };

  const handleDelete = (idx) => {
    setImageList(imageList.filter((_, index) => index !== idx));
  };

  const handleSubmit = () => {
    const formData = new FormData();
    const uploadImages = imageList.filter((img) => typeof img.url !== 'string');
    const uploadedImages = imageList
      .filter((img) => typeof img.url === 'string')
      .map((img) => img.url.split('?')[0])
      .filter(Boolean);

    formData.append('image_urls', uploadedImages.toString());
    uploadImages.forEach((s) => {
      formData.append('image', s.url);
    });
    setLoading(true);
    api
      .post(`delivery/${trackingCode}/${delivery.shipment.id}/update`, formData, { headers: { 'Content-Type': undefined } })
      .then((response) => {
        if (response.status === 200) {
          if (isOpenPreferredPhoto) {
            setIsOpeningPreferredPhoto(false);
            deliveryStore.updateField('isOpenPreferredPhoto', false);
            setFinishUpFeedback(true);
            setLoading(false);
            handleCancel();
          } else {
            deliveryStore.getDelivery(trackingCode, () => {
              setLoading(false);
              handleCancel();
            });
          }
        } else {
          setLoading(false);
        }
      });
  };

  const handleCancel = () => {
    if (isOpenPreferredPhoto) {
      setIsOpeningPreferredPhoto(false);
      deliveryStore.updateField('isOpenPreferredPhoto', false);
    } else {
      searchParams.set('dialog', 'closed');
      if (!skipQuestions) searchParams.set('skip_questionnaire', 'skippable');
      setSearchParams(searchParams);
    }
  };

  return (
    <StyledDialog open maxWidth="md" fullWidth>
      <DialogHeader title="Preferred Drop-off" />
      <DialogContent>
        <p className={classes.text}>
          While Jitsu will attempt to complete your delivery change requests, you will not be able to update the scheduled delivery date.
        </p>
        <Hr />
        <p className={classes.text}>
          Please provide a good delivery photo to help us deliver your package successfully! Photo examples can be your house, door, gate, pathway,
          etc.
        </p>
        <input
          type="file"
          hidden
          id="attachment"
          accept={IMAGE_TYPES.join(', ')}
          onChange={(e) => handleChange(e)}
          disabled={isConverting ? 'disabled' : ''}
        />
        {imageList.length > 0 ? (
          <>
            <div>
              <ImagesThumb pods={imageList} title="Preferred Drop-off" onDelete={handleDelete} />
            </div>
            <label htmlFor="attachment" className={classes.add}>
              + Add another photo
            </label>
          </>
        ) : (
          <div>
            <label htmlFor="attachment" className={classes.imageContainer}>
              <AddAPhotoIcon />
            </label>
          </div>
        )}
      </DialogContent>
      <DialogFooter>
        <DialogActionButton
          onClick={handleCancel}
          size="medium"
          color="primary"
          variant="outlined"
          disableElevation
          disableRipple
          disabled={isConverting}
        >
          Cancel
        </DialogActionButton>
        <DialogActionButton
          disabled={loading || isConverting}
          onClick={() => handleSubmit()}
          size="medium"
          color="primary"
          variant="contained"
          disableElevation
          disableRipple
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </DialogActionButton>
      </DialogFooter>
    </StyledDialog>
  );
});

export default PreferPhotoDialog;
